import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './DailyReport.css'

const today = () => new Date().toISOString().split('T')[0]

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']

function shortDay(dateStr) {
  return DAY_NAMES[new Date(dateStr + 'T12:00:00').getDay()]
}

function shortDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00')
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}

const VISITOR_SERIES = [
  { key: 'total',      label: 'Gesamt',        color: '#818cf8' },
  { key: 'checkedOut', label: 'Ausgecheckt',   color: '#66bb6a' },
  { key: 'onSite',     label: 'Noch vor Ort',  color: '#ef7d00' },
]

const CHECKOUT_SERIES = [
  { key: 'manual',     label: 'Manuell',       color: '#66bb6a' },
  { key: 'system',     label: 'System',        color: '#94a3b8' },
  { key: 'avgHours',   label: 'Ø Aufenthalt',  color: '#818cf8', unit: ' h' },
]

const APPROVAL_SERIES = [
  { key: 'approved',   label: 'Zugelassen',    color: '#66bb6a' },
  { key: 'rejected',   label: 'Abgelehnt',     color: '#e53935' },
  { key: 'expired',    label: 'Abgelaufen',    color: '#94a3b8' },
]

const CW = 560, CH = 168
const PL = 32, PR = 8, PT = 12, PB = 44
const PW = CW - PL - PR
const PH = CH - PT - PB

function exportChartAsPng(svgEl, filename) {
  const SCALE = 2
  const W = CW * SCALE, H = CH * SCALE

  const clone = svgEl.cloneNode(true)
  clone.setAttribute('width',  W)
  clone.setAttribute('height', H)
  clone.setAttribute('xmlns',  'http://www.w3.org/2000/svg')

  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
  bg.setAttribute('width', W); bg.setAttribute('height', H)
  bg.setAttribute('fill', '#1a1a1a')
  clone.insertBefore(bg, clone.firstChild)

  const svgStr = new XMLSerializer().serializeToString(clone)
    .replace(/var\(--[^,)]+,\s*([^)]+)\)/g, '$1')

  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const img  = new Image()
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = W; canvas.height = H
    canvas.getContext('2d').drawImage(img, 0, 0)
    URL.revokeObjectURL(url)
    const a = document.createElement('a')
    a.download = filename
    a.href = canvas.toDataURL('image/png')
    a.click()
  }
  img.src = url
}

function xAt(i, n)    { return PL + i * (PW / Math.max(n - 1, 1)) }
function yAt(v, maxY) { return PT + PH - (v / maxY) * PH }
function niceMax(v)   {
  if (v <= 0) return 5
  if (v <= 5) return 5
  if (v <= 10) return 10
  return Math.ceil(v / 5) * 5
}

function WeeklyChart({ data, series, title }) {
  const [active,   setActive]   = useState(() => Object.fromEntries(series.map(s => [s.key, true])))
  const [hoverIdx, setHoverIdx] = useState(null)
  const svgRef   = useRef(null)
  const todayStr = today()

  const n       = data.length
  const xi      = i => xAt(i, n)
  const halfSlot = PW / (2 * Math.max(n - 1, 1))
  const slotW    = PW / Math.max(n - 1, 1)

  const rawMax = Math.max(
    ...data.flatMap(d => series.filter(s => active[s.key]).map(s => d[s.key])),
    1
  )
  const maxY = niceMax(rawMax)

  const gridLines = [0.25, 0.5, 0.75, 1].map(f => ({
    y: yAt(maxY * f, maxY), val: Math.round(maxY * f),
  }))

  const labelStep = n <= 7 ? 1 : n <= 14 ? 2 : 5
  const dotR      = n > 14 ? 2 : 3.5

  function toggle(key) {
    setActive(a => {
      const next = { ...a, [key]: !a[key] }
      if (!Object.values(next).some(Boolean)) return a
      return next
    })
  }

  const hd = hoverIdx !== null ? data[hoverIdx] : null

  return (
    <div className="wchart">
      <div className="wchart-toggles">
        {series.map(s => (
          <button
            key={s.key}
            className={`wchart-toggle${active[s.key] ? ' wchart-toggle--on' : ''}`}
            style={{ '--sc': s.color, background: active[s.key] ? s.color + '22' : 'transparent' }}
            onClick={() => toggle(s.key)}
          >
            <span className="wchart-toggle-dot" style={{ background: s.color }} />
            {s.label}
          </button>
        ))}
      </div>

      <div className="wchart-svg-wrap">
        <button
          className="wchart-export-btn"
          title="Als PNG exportieren"
          onClick={() => svgRef.current && exportChartAsPng(svgRef.current, `${title || 'chart'}.png`)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </button>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CW} ${CH}`}
          className="wchart-svg"
        >
          {/* Grid lines */}
          {gridLines.map((g, i) => (
            <g key={i}>
              <line x1={PL} y1={g.y} x2={PL + PW} y2={g.y}
                stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
              <text x={PL - 6} y={g.y + 4} textAnchor="end"
                fontSize="10" fill="var(--text-muted, #888)">{g.val}</text>
            </g>
          ))}

          {/* X axis */}
          <line x1={PL} y1={PT + PH} x2={PL + PW} y2={PT + PH}
            stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

          {/* X labels — thinned out for month view */}
          {data.map((d, i) => {
            if (i % labelStep !== 0 && i !== n - 1) return null
            const x = xi(i)
            const isToday = d.date === todayStr
            const color = isToday ? '#ef7d00' : undefined
            return (
              <g key={d.date}>
                {n <= 10 && (
                  <text x={x} y={CH - 26} textAnchor="middle"
                    fontSize="11" fontWeight={isToday ? '700' : '500'}
                    fill={color || 'var(--text-primary, #ccc)'}>
                    {shortDay(d.date)}
                  </text>
                )}
                <text x={x} y={n <= 10 ? CH - 10 : CH - 16} textAnchor="middle"
                  fontSize="10" fill={color || 'var(--text-muted, #888)'}>
                  {shortDate(d.date)}
                </text>
              </g>
            )
          })}

          {/* Lines + dots */}
          {series.filter(s => active[s.key]).map(s => (
            <g key={s.key}>
              <polyline
                points={data.map((d, i) => `${xi(i)},${yAt(d[s.key], maxY)}`).join(' ')}
                fill="none" stroke={s.color}
                strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"
              />
              {data.map((d, i) => (
                <circle key={i}
                  cx={xi(i)} cy={yAt(d[s.key], maxY)}
                  r={hoverIdx === i ? 5 : dotR}
                  fill={s.color}
                  stroke={hoverIdx === i ? 'var(--bg-card, #1e1e1e)' : 'none'}
                  strokeWidth="2"
                />
              ))}
            </g>
          ))}

          {/* Hover crosshair */}
          {hoverIdx !== null && (
            <line
              x1={xi(hoverIdx)} y1={PT}
              x2={xi(hoverIdx)} y2={PT + PH}
              stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="4 3"
            />
          )}

          {/* Invisible hover zones */}
          {data.map((_, i) => (
            <rect key={i}
              x={xi(i) - halfSlot} y={PT} width={slotW} height={PH}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            />
          ))}
        </svg>

        {hd && (
          <div className="wchart-tooltip" style={{
            left: `${(xi(hoverIdx) / CW) * 100}%`,
            transform: `translateX(${hoverIdx >= n - 2 ? '-90%' : hoverIdx >= 2 ? '-50%' : '-5%'})`,
          }}>
            <div className="wchart-tip-date">{shortDay(hd.date)}, {shortDate(hd.date)}</div>
            {series.filter(s => active[s.key]).map(s => (
              <div key={s.key} className="wchart-tip-row">
                <span className="wchart-tip-dot" style={{ background: s.color }} />
                {s.label}: <strong>{hd[s.key]}{s.unit ?? ''}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function formatTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

function duration(inIso, outIso) {
  if (!inIso || !outIso) return '—'
  const min = Math.round((new Date(outIso) - new Date(inIso)) / 60000)
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m ? `${h} h ${m} min` : `${h} h`
}

function durationMs(inIso, outIso) {
  if (!inIso || !outIso) return -1
  return new Date(outIso) - new Date(inIso)
}

function exportCSV(rows, date) {
  const headers = ['Name', 'Firma', 'Besuchsgrund', 'Eincheck', 'Auscheck', 'Dauer', 'Typ', 'Status']
  const data = rows.map(r => [
    `${r.first_name} ${r.last_name}`,
    r.company || '',
    r.reason_for_visit || '',
    r.status === 'rejected' || r.status === 'expired' ? '—' : formatTime(r.created_at),
    formatTime(r.checked_out_at),
    duration(r.created_at, r.checked_out_at),
    r.flow_mode || '',
    r.status || '',
  ])
  const csv = [headers, ...data]
    .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `tagesbericht-${date}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const COLS = [
  { key: 'name',          label: 'Name' },
  { key: 'company',       label: 'Firma' },
  { key: 'reason',        label: 'Besuchsgrund' },
  { key: 'checkin',       label: 'Eincheck' },
  { key: 'checkout',      label: 'Auscheck' },
  { key: 'checkout_type', label: 'Checkout-Typ' },
  { key: 'duration',      label: 'Dauer' },
  { key: 'type',          label: 'Typ' },
]

function SortIcon({ col, sortKey, sortDir }) {
  if (sortKey !== col) return <span className="sort-icon">↕</span>
  return <span className="sort-icon sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>
}

export default function DailyReport() {
  const { apiFetch, admin, isOwner } = useAuth()

  const [mode,         setMode]         = useState('day')   // 'day' | 'range'
  const [date,         setDate]         = useState(today)
  const [dateFrom,     setDateFrom]     = useState(today)
  const [dateTo,       setDateTo]       = useState(today)
  const [tenants,      setTenants]      = useState([])
  const [tenantId,     setTenantId]     = useState(isOwner ? null : admin?.tenant_id)
  const [rows,         setRows]         = useState([])
  const [loading,      setLoading]      = useState(false)
  const [emailInput,   setEmailInput]   = useState('')
  const [emailSending, setEmailSending] = useState(false)
  const [emailMsg,     setEmailMsg]     = useState(null)
  const [filterText,   setFilterText]   = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [sortKey,      setSortKey]      = useState('checkin')
  const [sortDir,      setSortDir]      = useState('asc')
  const [weekStats,    setWeekStats]    = useState([])
  const [period,       setPeriod]       = useState('7d')
  const [visibleCharts, setVisibleCharts] = useState({ besucher: true, checkouts: true, zulassung: true })

  useEffect(() => {
    if (!isOwner) return
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setTenants(data)
          setTenantId(data[0].id)
        }
      })
      .catch(() => {})
  }, [isOwner])

  const load = useCallback(() => {
    if (!tenantId) return
    setLoading(true)
    const qs = period === 'custom'
      ? `from=${dateFrom}&to=${dateTo}`
      : `date=${date}`
    apiFetch(`/api/admin/checkins/${tenantId}?${qs}`)
      .then(r => r.json())
      .then(data => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [tenantId, date, dateFrom, dateTo, period])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!tenantId) return
    if (period === 'custom' && (!dateFrom || !dateTo)) return
    const qs = period === 'custom'
      ? `from=${dateFrom}&to=${dateTo}`
      : `days=${period === '30d' ? 30 : 7}`
    apiFetch(`/api/admin/report/weekly-stats/${tenantId}?${qs}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setWeekStats(data) })
      .catch(() => {})
  }, [tenantId, period, dateFrom, dateTo])

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const displayRows = useMemo(() => {
    let result = [...rows]

    if (filterText) {
      const q = filterText.toLowerCase()
      result = result.filter(r =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
        (r.company || '').toLowerCase().includes(q) ||
        (r.reason_for_visit || '').toLowerCase().includes(q) ||
        (r.flow_mode || '').toLowerCase().includes(q)
      )
    }

    if (filterStatus === 'onsite')   result = result.filter(r => !r.checked_out_at && (!r.status || r.status === 'approved'))
    if (filterStatus === 'out')      result = result.filter(r =>  !!r.checked_out_at)
    if (filterStatus === 'rejected') result = result.filter(r => r.status === 'rejected')
    if (filterStatus === 'expired')  result = result.filter(r => r.status === 'expired')

    result.sort((a, b) => {
      let va, vb
      switch (sortKey) {
        case 'name':
          va = `${a.first_name} ${a.last_name}`; vb = `${b.first_name} ${b.last_name}`; break
        case 'company':
          va = a.company || ''; vb = b.company || ''; break
        case 'reason':
          va = a.reason_for_visit || ''; vb = b.reason_for_visit || ''; break
        case 'checkin':
          va = a.created_at || ''; vb = b.created_at || ''; break
        case 'checkout':
          va = a.checked_out_at || ''; vb = b.checked_out_at || ''; break
        case 'checkout_type':
          va = a.checkout_type || ''; vb = b.checkout_type || ''; break
        case 'duration':
          va = durationMs(a.created_at, a.checked_out_at)
          vb = durationMs(b.created_at, b.checked_out_at)
          break
        case 'type':
          va = a.flow_mode || ''; vb = b.flow_mode || ''; break
        default:
          va = a.created_at || ''; vb = b.created_at || ''
      }
      const cmp = typeof va === 'number' ? va - vb : va.localeCompare(vb, 'de')
      return sortDir === 'asc' ? cmp : -cmp
    })

    return result
  }, [rows, filterText, filterStatus, sortKey, sortDir])

  const total      = rows.length
  const checkedOut = rows.filter(r => r.checked_out_at).length
  const rejected   = rows.filter(r => r.status === 'rejected').length
  const expired    = rows.filter(r => r.status === 'expired').length
  const onSite     = rows.filter(r => !r.checked_out_at && (!r.status || r.status === 'approved')).length
  const isFiltered = displayRows.length !== rows.length

  const handleSendEmail = async () => {
    const recipients = emailInput.split(',').map(e => e.trim()).filter(Boolean)
    if (!recipients.length) return
    setEmailSending(true)
    setEmailMsg(null)
    try {
      const body = { date, recipients }
      if (isOwner) body.tenantId = tenantId
      const res = await apiFetch('/api/admin/report/email', {
        method: 'POST',
        body:   JSON.stringify(body),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Fehler')
      }
      setEmailMsg({ type: 'success', text: 'Bericht wurde versendet.' })
      setEmailInput('')
    } catch (err) {
      setEmailMsg({ type: 'error', text: err.message })
    } finally {
      setEmailSending(false)
      setTimeout(() => setEmailMsg(null), 5000)
    }
  }

  const formattedDate = new Date(date + 'T12:00:00').toLocaleDateString('de-DE', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  })

  return (
    <div className="report-root">
      <div className="page-header report-header-screen">
        <h1>Tagesbericht</h1>
        <p>Übersicht aller Besucher an einem Tag.</p>
      </div>

      {/* Controls — hidden when printing */}
      <div className="report-controls report-screen-only">
        <div className="card report-controls-card">
          <div className="report-controls-row">
            {isOwner && tenants.length > 0 && (
              <select
                className="form-control report-field"
                value={tenantId || ''}
                onChange={e => setTenantId(e.target.value)}
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                ))}
              </select>
            )}
            <input
              type="date"
              className="form-control report-field"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
            <button className="btn btn-ghost"
              onClick={() => exportCSV(displayRows, mode === 'range' ? `${dateFrom}_${dateTo}` : date)}
              disabled={!displayRows.length}>
              CSV exportieren
            </button>
            <button className="btn btn-ghost" onClick={() => window.print()}>
              Drucken / PDF
            </button>
          </div>

          {/* Filter row */}
          <div className="report-filter-row">
            <input
              type="text"
              className="form-control report-filter-input"
              placeholder="Suchen nach Name, Firma, Besuchsgrund…"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
            <select
              className="form-control report-filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="all">Alle Besucher</option>
              <option value="onsite">Noch vor Ort</option>
              <option value="out">Ausgecheckt</option>
              <option value="rejected">Abgelehnt</option>
              <option value="expired">Abgelaufen</option>
            </select>
          </div>

          {/* Email row */}
          <div className="report-email-row">
            <input
              type="text"
              className="form-control report-email-input"
              placeholder="E-Mail-Adressen (kommagetrennt)"
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
            />
            <button
              className="btn btn-primary"
              onClick={handleSendEmail}
              disabled={emailSending || !emailInput.trim() || !rows.length}
            >
              {emailSending ? <><span className="spinner spinner-sm" /> Senden…</> : 'Per E-Mail senden'}
            </button>
          </div>
          {emailMsg && (
            <div className={`alert ${emailMsg.type === 'success' ? 'alert-success' : 'alert-error'} report-email-msg`}>
              {emailMsg.text}
            </div>
          )}
        </div>
      </div>

      {/* Weekly charts — screen only */}
      {weekStats.length > 0 && (
        <div className="report-screen-only wchart-section">
          <div className="wchart-period-row">
            <div className="wchart-toggle-group">
              {[
                { key: 'besucher',  label: 'Besucher' },
                { key: 'checkouts', label: 'Checkouts' },
                { key: 'zulassung', label: 'Zulassung' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  className={`wchart-period-btn${visibleCharts[key] ? ' wchart-period-btn--shown' : ''}`}
                  onClick={() => setVisibleCharts(v => ({ ...v, [key]: !v[key] }))}
                >
                  {visibleCharts[key] ? '✓ ' : ''}{label}
                </button>
              ))}
            </div>
            <div className="wchart-period-right">
              <div className="wchart-toggle-group">
                <button
                  className={`wchart-period-btn${period === '7d'     ? ' wchart-period-btn--active' : ''}`}
                  onClick={() => setPeriod('7d')}
                >7 Tage</button>
                <button
                  className={`wchart-period-btn${period === '30d'    ? ' wchart-period-btn--active' : ''}`}
                  onClick={() => setPeriod('30d')}
                >30 Tage</button>
                <button
                  className={`wchart-period-btn${period === 'custom' ? ' wchart-period-btn--active' : ''}`}
                  onClick={() => setPeriod('custom')}
                >Zeitraum</button>
              </div>
              {period === 'custom' && (
                <div className="wchart-custom-range">
                  <input
                    type="date"
                    className="form-control"
                    value={dateFrom}
                    max={dateTo}
                    onChange={e => setDateFrom(e.target.value)}
                  />
                  <span className="wchart-range-sep">–</span>
                  <input
                    type="date"
                    className="form-control"
                    value={dateTo}
                    min={dateFrom}
                    onChange={e => setDateTo(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {(visibleCharts.besucher || visibleCharts.checkouts || visibleCharts.zulassung) && (
            <div className="wchart-row">
              {visibleCharts.besucher && (
                <div className="card wchart-card">
                  <p className="section-title wchart-card-title">Besucher</p>
                  <WeeklyChart data={weekStats} series={VISITOR_SERIES} title="besucher" />
                </div>
              )}
              {visibleCharts.checkouts && (
                <div className="card wchart-card">
                  <p className="section-title wchart-card-title">Checkouts & Aufenthalt</p>
                  <WeeklyChart data={weekStats} series={CHECKOUT_SERIES} title="checkouts-aufenthalt" />
                </div>
              )}
              {visibleCharts.zulassung && (
                <div className="card wchart-card">
                  <p className="section-title wchart-card-title">Zulassung</p>
                  <WeeklyChart data={weekStats} series={APPROVAL_SERIES} title="zulassung" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report content — visible on screen and when printing */}
      <div className="card report-body">
        {/* Print header — only visible when printing */}
        <div className="report-print-header">
          <h2 className="report-print-title">Tagesbericht viGuard</h2>
          <p className="report-print-date">{formattedDate}</p>
        </div>

        {/* Summary */}
        <div className="report-summary">
          <div
            className={`report-stat report-stat--clickable${filterStatus === 'all' ? ' report-stat--active' : ''}`}
            onClick={() => setFilterStatus('all')}
            title="Alle Besucher anzeigen"
          >
            <div className="report-stat-value">{total}</div>
            <div className="report-stat-label">Besucher gesamt</div>
          </div>
          <div
            className={`report-stat report-stat--clickable${filterStatus === 'out' ? ' report-stat--active report-stat--active-out' : ''}`}
            onClick={() => setFilterStatus(s => s === 'out' ? 'all' : 'out')}
            title="Nur ausgecheckte Besucher anzeigen"
          >
            <div className="report-stat-value report-stat-value--out">{checkedOut}</div>
            <div className="report-stat-label">Ausgecheckt</div>
          </div>
          <div
            className={`report-stat report-stat--clickable${filterStatus === 'onsite' ? ' report-stat--active report-stat--active-in' : ''}`}
            onClick={() => setFilterStatus(s => s === 'onsite' ? 'all' : 'onsite')}
            title="Nur Besucher vor Ort anzeigen"
          >
            <div className="report-stat-value report-stat-value--in">{onSite}</div>
            <div className="report-stat-label">Noch vor Ort</div>
          </div>
          {rejected > 0 && (
            <div
              className={`report-stat report-stat--clickable${filterStatus === 'rejected' ? ' report-stat--active report-stat--active-rejected' : ''}`}
              onClick={() => setFilterStatus(s => s === 'rejected' ? 'all' : 'rejected')}
              title="Nur abgelehnte Besucher anzeigen"
            >
              <div className="report-stat-value report-stat-value--rejected">{rejected}</div>
              <div className="report-stat-label">Abgelehnt</div>
            </div>
          )}
          {expired > 0 && (
            <div
              className={`report-stat report-stat--clickable${filterStatus === 'expired' ? ' report-stat--active report-stat--active-expired' : ''}`}
              onClick={() => setFilterStatus(s => s === 'expired' ? 'all' : 'expired')}
              title="Nur abgelaufene Voranmeldungen anzeigen"
            >
              <div className="report-stat-value report-stat-value--expired">{expired}</div>
              <div className="report-stat-label">Abgelaufen</div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="loading-state"><span className="spinner" /> Laden…</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>Keine Besucher an diesem Tag.</p>
          </div>
        ) : displayRows.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <p>Keine Einträge entsprechen dem Filter.</p>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="report-table">
                <thead>
                  <tr>
                    {COLS.map(col => (
                      <th
                        key={col.key}
                        className="report-th-sort"
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        <SortIcon col={col.key} sortKey={sortKey} sortDir={sortDir} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map(r => (
                    <tr key={r.session_id}>
                      <td>{r.first_name} {r.last_name}</td>
                      <td>{r.company || '—'}</td>
                      <td>{r.reason_for_visit || '—'}</td>
                      <td>{r.status === 'rejected' || r.status === 'expired' ? '—' : formatTime(r.created_at)}</td>
                      <td>{formatTime(r.checked_out_at)}</td>
                      <td>
                        {r.checkout_type === 'manual' && <span className="badge badge-green">Manuell</span>}
                        {r.checkout_type === 'system' && <span className="badge badge-grey">System</span>}
                        {!r.checkout_type && '—'}
                      </td>
                      <td>{duration(r.created_at, r.checked_out_at)}</td>
                      <td>{r.flow_mode || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isFiltered && (
              <p className="report-filter-count report-screen-only">
                {displayRows.length} von {rows.length} Einträgen
              </p>
            )}
          </>
        )}

        {/* Print footer */}
        <div className="report-print-footer">
          Erstellt am {new Date().toLocaleString('de-DE')} · viGuard
        </div>
      </div>
    </div>
  )
}
